import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { api } from '@/lib/api'
import { Loader2, AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ExecSalesDashboardMethod } from '@/components/reports/ExecSalesDashboardMethod'
import { explainError } from '@/lib/explain-error'

export default function ExecSalesDashboard() {
  const [html, setHtml] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDashboard = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Use the exported `api` axios instance with { responseType: 'text' }
        // to fetch the HTML. The instance attaches the auth token automatically.
        const response = await api.get<string>('/reports/exec-sales-dashboard', {
          responseType: 'text',
        })
        setHtml(response.data)
      } catch (err) {
        setError(explainError(err, "Couldn't load the dashboard"))
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboard()
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Exec Sales Dashboard</h1>
        <p className="text-muted-foreground">Executive sales metrics and insights</p>
      </div>

      <ExecSalesDashboardMethod />

      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2
              className="h-8 w-8 animate-spin text-muted-foreground"
              aria-label="Loading dashboard"
            />
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {html && (
        <iframe
          title="Exec Sales Dashboard"
          srcDoc={html}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          className="w-full border-0 rounded-lg"
          style={{ height: 'calc(100vh - 200px)' }}
        />
      )}
    </div>
  )
}
